import { useEffect, useState } from "react";
import {
  FiPlus,
  FiTrash2,
  FiEdit2,
  FiCheck,
  FiX,
  FiCopy,
  FiUser,
  FiCalendar,
  FiGitPullRequest,
  FiMenu,
  FiEye,
  FiEyeOff,
} from "react-icons/fi";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { Container, Button, Form, Card } from "react-bootstrap";
import { GoTasklist } from "react-icons/go";
import TaskThreadPanel from "./TaskThreadPanel";
import { projectManagementServices } from "../../APIServices/WorkBridge/ProjectManagement/projectManagementServices";

const ProjectManagement = ({ gpId }) => {

  const [sections, setSections] = useState([]);

  const [editingSection, setEditingSection] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [newTaskInput, setNewTaskInput] = useState({});
  const [showTaskInput, setShowTaskInput] = useState({});
  const [editingTask, setEditingTask] = useState(null);
  const [editTaskTitle, setEditTaskTitle] = useState("");
  const [selectedTaskDetail, setSelectedTaskDetail] = useState(null);
  const [editingSubtaskId, setEditingSubtaskId] = useState(null);
  const [editingSubtaskText, setEditingSubtaskText] = useState("");
  const [contextMenu, setContextMenu] = useState(null);
  const [sectionMenu, setSectionMenu] = useState(null);
  const [hideEmptySections, setHideEmptySections] = useState({});
  const [sectionRules, setSectionRules] = useState({});

  const getTaskById = (sectionId, taskId) => {
    const section = sections.find((s) => s.id === sectionId);
    return section?.tasks.find((t) => t.id === taskId);
  };

  const handleDragEnd = (result) => {
    const { source, destination, type } = result;

    if (!destination) return;

    // SECTION DRAG (horizontal)
    if (type === "SECTION") {
      const newSections = Array.from(sections);
      const [movedSection] = newSections.splice(source.index, 1);
      newSections.splice(destination.index, 0, movedSection);
      setSections(newSections);
      return;
    }

    // TASK DRAG
    const sourceSectionIndex = sections.findIndex(
      (s) => s.id === source.droppableId
    );
    const destSectionIndex = sections.findIndex(
      (s) => s.id === destination.droppableId
    );

    const sourceSection = sections[sourceSectionIndex];
    const destSection = sections[destSectionIndex];

    const sourceTasks = Array.from(sourceSection.tasks);
    const destTasks =
      sourceSection === destSection
        ? sourceTasks
        : Array.from(destSection.tasks);

    const [movedTask] = sourceTasks.splice(source.index, 1);
    destTasks.splice(destination.index, 0, movedTask);

    const newSections = Array.from(sections);
    newSections[sourceSectionIndex] = {
      ...sourceSection,
      tasks: sourceTasks,
    };
    newSections[destSectionIndex] = {
      ...destSection,
      tasks: destTasks,
    };

    setSections(newSections);
  };

  const addUpdateProjectTask = async (sectionId) => {
    const title = newTaskInput[sectionId] || "";
    if (!title.trim()) return;
    const taskId = `TASK${Math.random().toString().substr(2, 10)}`;
    const newTask = {
      id: taskId,
      title,
    };
    const updatedSections = sections.map((section) => {
      if (section.id === sectionId) {
        return {
          ...section,
          tasks: [...section.tasks, newTask],
        };
      }
      return section;
    });
    setSections(updatedSections);
    setNewTaskInput({ ...newTaskInput, [sectionId]: "" });

    const userData = {
      gpId,
      sectionId,
      task: newTask,
      action: "add"
    };

    await projectManagementServices.addUpdateProjectTask(userData);

  };

  const deleteProjectTask = async (sectionId, taskId) => {
    const updatedSections = sections.map((section) => {
      if (section.id === sectionId) {
        return {
          ...section,
          tasks: section.tasks.filter((task) => task.id !== taskId),
        };
      }
      return section;
    });
    setSections(updatedSections);
    const userData = {
      gpId,
      sectionId,
      task: { id: taskId },
      action: "delete"
    };

    await projectManagementServices.addUpdateProjectTask(userData);

  };

  const deleteProjectSectionData = async (sectionId) => {
    setSections(sections.filter((s) => s.id !== sectionId));
    const userData = {
      gpId,
      sectionId,
    };

    await projectManagementServices.deleteProjectSection(userData);
  };

  const startEditTitle = (sectionId, currentTitle) => {
    setEditingSection(sectionId);
    setEditTitle(currentTitle);
  };

  const updateProjectSectionTitle = async (sectionId) => {
    const updatedSections = sections.map((section) => {
      if (section.id === sectionId) {
        return { ...section, title: editTitle };
      }
      return section;
    });
    setSections(updatedSections);
    const userData = {
      gpId,
      sectionId,
      title: editTitle,
    };

    await projectManagementServices.updateProjectSection(userData);
    setEditingSection(null);
    setEditTitle("");
  };

  const cancelEdit = () => {
    setEditingSection(null);
    setEditTitle("");
  };

  const getProjectManagementList = async (gpId) => {
    let data = await projectManagementServices.getProjectManagementList(gpId)
    setSections(data.data || [])
  }

  const addNewProjectSection = async () => {
    let userData = {}
    let sectionId = `SECTION${Math.random().toString().substr(2, 10)}`
    let title = "New Section"
    const newSection = {
      id: sectionId,
      title: title,
      tasks: [],
    };
    userData.gpId = gpId
    userData.sectionId = sectionId
    userData.title = title
    setSections((prevSections) => [...prevSections, newSection]);
    await projectManagementServices.addNewProjectSection(userData)
  };

  const addNewProjectSectionAfter = async (sectionId) => {
    let userData = {}
    let uniqueId = `SECTION${Math.random().toString().substr(2, 10)}`
    let title = "New Section"
    const sectionIndex = sections.findIndex((s) => s.id === sectionId);
    const newSection = {
      id: uniqueId,
      title: title,
      tasks: [],
    };
    const newSections = [
      ...sections.slice(0, sectionIndex + 1),
      newSection,
      ...sections.slice(sectionIndex + 1),
    ];
    userData.gpId = gpId
    userData.sectionId = uniqueId
    userData.title = title
    setSections(newSections);
    await projectManagementServices.addNewProjectSection(userData)
  };

  const addNewProjectSectionBefore = async (sectionId) => {
    let userData = {}
    let uniqueId = `SECTION${Math.random().toString().substr(2, 10)}`
    let title = "New Section"
    const sectionIndex = sections.findIndex((s) => s.id === sectionId);
    const newSection = {
      id: `SECTION${Math.random().toString().substr(2, 10)}`,
      title: "New Section",
      tasks: [],
    };
    const newSections = [
      ...sections.slice(0, sectionIndex),
      newSection,
      ...sections.slice(sectionIndex),
    ];
    userData.gpId = gpId
    userData.sectionId = uniqueId
    userData.title = title
    setSections(newSections);
    await projectManagementServices.addNewProjectSection(userData)
  };

  const startEditTask = (sectionId, taskId, currentTitle) => {
    setEditingTask(`${sectionId}-${taskId}`);
    setEditTaskTitle(currentTitle);
  };

  const updateProjectTask = async (sectionId, taskId) => {
    const section = sections.find((s) => s.id === sectionId);
    const existingTask = section?.tasks.find((t) => t.id === taskId);
    if (!existingTask) return;
    const updatedTask = {
      ...existingTask,
      taskId: taskId,
      title: editTaskTitle,
    };
    setSections((prev) =>
      prev.map((section) =>
        section.id === sectionId
          ? {
            ...section,
            tasks: section.tasks.map((task) =>
              task.id === taskId
                ? { ...task, title: editTaskTitle }
                : task
            ),
          }
          : section
      )
    );
    await projectManagementServices.addUpdateProjectTask({
      gpId,
      sectionId,
      task: updatedTask,
      action: "update"
    });
    setEditingTask(null);
    setEditTaskTitle("");
  };

  const cancelTaskEdit = () => {
    setEditingTask(null);
    setEditTaskTitle("");
  };

  const toggleTaskDone = (sectionId, taskId) => {
    const updatedSections = sections.map((section) => {
      if (section.id === sectionId) {
        return {
          ...section,
          tasks: section.tasks.map((task) =>
            task.id === taskId ? { ...task, done: !task.done } : task
          ),
        };
      }
      return section;
    });
    setSections(updatedSections);
  };

  const addProjectSubtask = async (sectionId, taskId) => {
    const subtaskId = `SUBTASK${Math.random().toString().substr(2, 10)}`;
    const newSubtask = {
      id: subtaskId,
      title: "New Subtask",
    };
    const updatedSections = sections.map((section) => {
      if (section.id === sectionId) {
        return {
          ...section,
          tasks: section.tasks.map((task) => {
            if (task.id === taskId) {
              return {
                ...task,
                subtasks: [...(task.subtasks || []), newSubtask],
              };
            }
            return task;
          }),
        };
      }
      return section;
    });
    setSections(updatedSections);
    const userData = {
      gpId,
      sectionId,
      taskId,
      subtask: newSubtask,
      action: "add"
    };
    await projectManagementServices.addUpdateProjectSubTask(userData);
  };

  const duplicateProjectTask = async (sectionId, taskId) => {
    let newTask = null;
    const updatedSections = sections.map((section) => {
      if (section.id === sectionId) {
        const taskToDuplicate = section.tasks.find((t) => t.id === taskId);
        if (taskToDuplicate) {
          newTask = {
            ...taskToDuplicate,
            id: `TASK${Math.random().toString().substr(2, 10)}`,
            title: `${taskToDuplicate.title} (Copy)`,
            subtasks: taskToDuplicate.subtasks
              ? taskToDuplicate.subtasks.map((s) => ({
                ...s,
                id: `SUBTASK${Math.random().toString().substr(2, 10)}`,
              }))
              : [],
          };
          return {
            ...section,
            tasks: [...section.tasks, newTask],
          };
        }
      }
      return section;
    });
    setSections(updatedSections);
    if (newTask) {
      await projectManagementServices.addUpdateProjectTask({
        gpId,
        sectionId,
        task: newTask,
        action: "add"
      });
    }
  };

  const updateProjectSubtask = async (sectionId, taskId, subtaskId) => {
    const section = sections.find((s) => s.id === sectionId);
    const task = section?.tasks.find((t) => t.id === taskId);
    const existingSubtask = task?.subtasks?.find(
      (st) => st.id === subtaskId
    );

    if (!existingSubtask) return;

    const updatedSubtask = {
      ...existingSubtask,
      subtaskId: subtaskId, // backend ke liye
      title: editingSubtaskText,
    };

    // ✅ Optimistic UI update
    setSections((prev) =>
      prev.map((section) =>
        section.id === sectionId
          ? {
            ...section,
            tasks: section.tasks.map((task) =>
              task.id === taskId
                ? {
                  ...task,
                  subtasks: task.subtasks.map((subtask) =>
                    subtask.id === subtaskId
                      ? { ...subtask, title: editingSubtaskText }
                      : subtask
                  ),
                }
                : task
            ),
          }
          : section
      )
    );
    await projectManagementServices.addUpdateProjectSubTask({
      gpId,
      sectionId,
      taskId,
      subtask: updatedSubtask,
      action: "update"
    });
    setEditingSubtaskId(null);
    setEditingSubtaskText("");
  };

  const deleteProjectSubtask = async (sectionId, taskId, subtaskId) => {
    const updatedSections = sections.map((section) => {
      if (section.id === sectionId) {
        return {
          ...section,
          tasks: section.tasks.map((task) => {
            if (task.id === taskId && task.subtasks) {
              return {
                ...task,
                subtasks: task.subtasks.filter((s) => s.id !== subtaskId),
              };
            }
            return task;
          }),
        };
      }
      return section;
    });
    setSections(updatedSections);
    await projectManagementServices.addUpdateProjectSubTask({
      gpId,
      sectionId,
      taskId,
      subtask: { id: subtaskId },
      action: "delete"
    });
  };

  const handleContextMenu = (e, type, data) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      type,
      data,
    });
  };

  const closeContextMenu = () => {
    setContextMenu(null);
  };

  useEffect(() => {
    if (gpId) {
      getProjectManagementList(gpId)
    }
  }, [gpId]);

  return (
    <div className="project-management-container" style={{ display: "flex", height: "100vh", width: "100%" }} onClick={closeContextMenu}>
      {sections?.length > 0 ?
        <>
          {/* Context Menu */}
          {contextMenu && contextMenu.visible && (
            <div
              style={{
                position: "fixed",
                top: contextMenu.y,
                left: contextMenu.x,
                backgroundColor: "white",
                border: "1px solid #ddd",
                borderRadius: "4px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                zIndex: 1000,
                minWidth: "150px",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {contextMenu.type === "section" && (
                <>
                  <div
                    style={{
                      padding: "8px 12px",
                      cursor: "pointer",
                      fontSize: "12px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      borderBottom: "1px solid #eee",
                    }}
                    onClick={() => {
                      startEditTitle(
                        contextMenu.data.sectionId,
                        contextMenu.data.title
                      );
                      closeContextMenu();
                    }}
                    onMouseEnter={(e) =>
                      (e.target.style.backgroundColor = "#f5f5f5")
                    }
                    onMouseLeave={(e) =>
                      (e.target.style.backgroundColor = "white")
                    }
                  >
                    <FiEdit2 size={14} /> Edit Title
                  </div>
                  <div
                    style={{
                      padding: "8px 12px",
                      cursor: "pointer",
                      fontSize: "12px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      borderBottom: "1px solid #eee",
                    }}
                    onClick={() => {
                      addNewProjectSectionAfter(contextMenu.data.sectionId);
                      closeContextMenu();
                    }}
                    onMouseEnter={(e) =>
                      (e.target.style.backgroundColor = "#f5f5f5")
                    }
                    onMouseLeave={(e) =>
                      (e.target.style.backgroundColor = "white")
                    }
                  >
                    <FiPlus size={14} /> Add Section After
                  </div>
                  <div
                    style={{
                      padding: "8px 12px",
                      cursor: "pointer",
                      fontSize: "12px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      color: "#f44336",
                    }}
                    onClick={() => {
                      deleteProjectSectionData(contextMenu.data.sectionId);
                      closeContextMenu();
                    }}
                    onMouseEnter={(e) =>
                      (e.target.style.backgroundColor = "#f5f5f5")
                    }
                    onMouseLeave={(e) =>
                      (e.target.style.backgroundColor = "white")
                    }
                  >
                    <FiTrash2 size={14} /> Delete Section
                  </div>
                </>
              )}

              {contextMenu.type === "task" && (
                <>
                  <div
                    style={{
                      padding: "8px 12px",
                      cursor: "pointer",
                      fontSize: "12px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      borderBottom: "1px solid #eee",
                    }}
                    onClick={() => {
                      setSelectedTaskDetail({
                        sectionId: contextMenu.data.sectionId,
                        taskId: contextMenu.data.taskId,
                        task: contextMenu.data.task,
                      });
                      closeContextMenu();
                    }}
                    onMouseEnter={(e) =>
                      (e.target.style.backgroundColor = "#f5f5f5")
                    }
                    onMouseLeave={(e) =>
                      (e.target.style.backgroundColor = "white")
                    }
                  >
                    <FiGitPullRequest size={14} /> View Details
                  </div>
                  <div
                    style={{
                      padding: "8px 12px",
                      cursor: "pointer",
                      fontSize: "12px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      borderBottom: "1px solid #eee",
                    }}
                    onClick={() => {
                      startEditTask(
                        contextMenu.data.sectionId,
                        contextMenu.data.taskId,
                        contextMenu.data.task.title
                      );
                      closeContextMenu();
                    }}
                    onMouseEnter={(e) =>
                      (e.target.style.backgroundColor = "#f5f5f5")
                    }
                    onMouseLeave={(e) =>
                      (e.target.style.backgroundColor = "white")
                    }
                  >
                    <FiEdit2 size={14} /> Edit Task
                  </div>
                  <div
                    style={{
                      padding: "8px 12px",
                      cursor: "pointer",
                      fontSize: "12px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      borderBottom: "1px solid #eee",
                    }}
                    onClick={() => {
                      addProjectSubtask(
                        contextMenu.data.sectionId,
                        contextMenu.data.taskId
                      );
                      closeContextMenu();
                    }}
                    onMouseEnter={(e) =>
                      (e.target.style.backgroundColor = "#f5f5f5")
                    }
                    onMouseLeave={(e) =>
                      (e.target.style.backgroundColor = "white")
                    }
                  >
                    <FiPlus size={14} /> Add Subtask
                  </div>
                  <div
                    style={{
                      padding: "8px 12px",
                      cursor: "pointer",
                      fontSize: "12px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      borderBottom: "1px solid #eee",
                    }}
                    onClick={() => {
                      duplicateProjectTask(
                        contextMenu.data.sectionId,
                        contextMenu.data.taskId
                      );
                      closeContextMenu();
                    }}
                    onMouseEnter={(e) =>
                      (e.target.style.backgroundColor = "#f5f5f5")
                    }
                    onMouseLeave={(e) =>
                      (e.target.style.backgroundColor = "white")
                    }
                  >
                    <FiCopy size={14} /> Duplicate Task
                  </div>
                  <div
                    style={{
                      padding: "8px 12px",
                      cursor: "pointer",
                      fontSize: "12px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      color: "#f44336",
                    }}
                    onClick={() => {
                      deleteProjectTask(contextMenu.data.sectionId, contextMenu.data.taskId);
                      closeContextMenu();
                    }}
                    onMouseEnter={(e) =>
                      (e.target.style.backgroundColor = "#f5f5f5")
                    }
                    onMouseLeave={(e) =>
                      (e.target.style.backgroundColor = "white")
                    }
                  >
                    <FiTrash2 size={14} /> Delete Task
                  </div>
                </>
              )}
            </div>
          )}
          <Container fluid className="chat-messages" style={{}}>
            <DragDropContext onDragEnd={handleDragEnd}>
              <div
                className="d-flex gap-3 overflow-auto position-fixed"
                style={{ width: "-webkit-fill-available" }}
              >
                <Droppable
                  droppableId="SECTIONS"
                  type="SECTION"
                  direction="horizontal"
                >
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className="d-flex gap-1"
                    >
                      {sections.map((section, sectionIndex) => (
                        <Draggable
                          key={section.id}
                          draggableId={section.id}
                          index={sectionIndex}
                        >
                          {(provided, snapshot) => (
                            <Card
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className="border shadow-sm"
                              style={{
                                width: "350px",
                                marginRight: 9,
                                backgroundColor: snapshot.isDragging
                                  ? "#e3f2fd"
                                  : "#fff",
                                ...provided.draggableProps.style,
                              }}
                            >
                              <Card.Header
                                {...provided.dragHandleProps}
                                className="d-flex align-items-center border-bottom gap-2"
                                style={{ cursor: "move" }}
                                onContextMenu={(e) =>
                                  handleContextMenu(e, "section", {
                                    sectionId: section.id,
                                    title: section.title,
                                  })
                                }
                              >
                                <GoTasklist size={16} />
                                {editingSection === section.id ? (
                                  <div className="d-flex gap-1 flex-grow-1">
                                    <Form.Control
                                      type="text"
                                      value={editTitle}
                                      onChange={(e) => setEditTitle(e.target.value)}
                                      size="sm"
                                      className="flex-grow-1"
                                      style={{ fontSize: "13px", fontWeight: "bold" }}
                                      autoFocus
                                    />
                                    <Button
                                      onClick={() => updateProjectSectionTitle(section.id)}
                                      variant="success"
                                      size="sm"
                                      style={{ fontSize: "16px" }}
                                    >
                                      <FiCheck />
                                    </Button>
                                    <Button
                                      onClick={cancelEdit}
                                      variant="danger"
                                      size="sm"
                                      style={{ fontSize: "16px" }}
                                    >
                                      <FiX />
                                    </Button>
                                  </div>
                                ) : (
                                  <>
                                    <h6
                                      className="m-0 flex-grow-1"
                                      style={{
                                        fontSize: "14px",
                                        fontWeight: "bold",
                                        color: "#333",
                                      }}
                                    >
                                      {section.title} ({section.tasks.length})
                                    </h6>
                                    <Button
                                      onClick={() =>
                                        setShowTaskInput({
                                          ...showTaskInput,
                                          [section.id]: true,
                                        })
                                      }
                                      variant="light"
                                      size="sm"
                                      style={{ color: "#4CAF50" }}
                                      title="Add task"
                                    >
                                      <FiPlus />
                                    </Button>
                                    <div style={{ position: "relative" }}>
                                      <Button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSectionMenu(
                                            sectionMenu?.sectionId === section.id
                                              ? null
                                              : { sectionId: section.id, visible: true }
                                          );
                                        }}
                                        variant="light"
                                        size="sm"
                                        style={{ color: "#666" }}
                                        title="Section menu"
                                      >
                                        <FiMenu />
                                      </Button>
                                      {sectionMenu &&
                                        sectionMenu.sectionId === section.id &&
                                        sectionMenu.visible && (
                                          <div
                                            style={{
                                              position: "absolute",
                                              top: "100%",
                                              right: 0,
                                              backgroundColor: "white",
                                              border: "1px solid #ddd",
                                              borderRadius: "4px",
                                              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                              zIndex: 1000,
                                              minWidth: "160px",
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <div
                                              style={{
                                                padding: "8px 12px",
                                                cursor: "pointer",
                                                fontSize: "12px",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px",
                                                borderBottom: "1px solid #eee",
                                              }}
                                              onClick={() => {
                                                addNewProjectSectionBefore(section.id);
                                                setSectionMenu(null);
                                              }}
                                              onMouseEnter={(e) =>
                                              (e.target.style.backgroundColor =
                                                "#f5f5f5")
                                              }
                                              onMouseLeave={(e) =>
                                              (e.target.style.backgroundColor =
                                                "white")
                                              }
                                            >
                                              <FiPlus size={14} /> Add Left
                                            </div>
                                            <div
                                              style={{
                                                padding: "8px 12px",
                                                cursor: "pointer",
                                                fontSize: "12px",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px",
                                                borderBottom: "1px solid #eee",
                                              }}
                                              onClick={() => {
                                                addNewProjectSectionAfter(section.id);
                                                setSectionMenu(null);
                                              }}
                                              onMouseEnter={(e) =>
                                              (e.target.style.backgroundColor =
                                                "#f5f5f5")
                                              }
                                              onMouseLeave={(e) =>
                                              (e.target.style.backgroundColor =
                                                "white")
                                              }
                                            >
                                              <FiPlus size={14} /> Add Right
                                            </div>
                                            <div
                                              style={{
                                                padding: "8px 12px",
                                                cursor: "pointer",
                                                fontSize: "12px",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px",
                                                borderBottom: "1px solid #eee",
                                              }}
                                              onClick={() => {
                                                startEditTitle(
                                                  section.id,
                                                  section.title
                                                );
                                                setSectionMenu(null);
                                              }}
                                              onMouseEnter={(e) =>
                                              (e.target.style.backgroundColor =
                                                "#f5f5f5")
                                              }
                                              onMouseLeave={(e) =>
                                              (e.target.style.backgroundColor =
                                                "white")
                                              }
                                            >
                                              <FiEdit2 size={14} /> Rename
                                            </div>
                                            <div
                                              style={{
                                                padding: "8px 12px",
                                                cursor: "pointer",
                                                fontSize: "12px",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px",
                                                borderBottom: "1px solid #eee",
                                              }}
                                              onClick={() => {
                                                setHideEmptySections({
                                                  ...hideEmptySections,
                                                  [section.id]: !hideEmptySections[
                                                    section.id
                                                  ],
                                                });
                                                setSectionMenu(null);
                                              }}
                                              onMouseEnter={(e) =>
                                              (e.target.style.backgroundColor =
                                                "#f5f5f5")
                                              }
                                              onMouseLeave={(e) =>
                                              (e.target.style.backgroundColor =
                                                "white")
                                              }
                                            >
                                              {hideEmptySections[section.id] ? (
                                                <>
                                                  <FiEyeOff size={14} /> Show Empty
                                                </>
                                              ) : (
                                                <>
                                                  <FiEye size={14} /> Hide Empty
                                                </>
                                              )}
                                            </div>
                                            <div
                                              style={{
                                                padding: "8px 12px",
                                                cursor: "pointer",
                                                fontSize: "12px",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px",
                                                borderBottom: "1px solid #eee",
                                              }}
                                              onClick={() => {
                                                setSectionRules({
                                                  ...sectionRules,
                                                  [section.id]: "rule_added",
                                                });
                                                alert(
                                                  "Add rule feature (customizable)"
                                                );
                                                setSectionMenu(null);
                                              }}
                                              onMouseEnter={(e) =>
                                              (e.target.style.backgroundColor =
                                                "#f5f5f5")
                                              }
                                              onMouseLeave={(e) =>
                                              (e.target.style.backgroundColor =
                                                "white")
                                              }
                                            >
                                              <FiPlus size={14} /> Add Rule
                                            </div>
                                            <div
                                              style={{
                                                padding: "8px 12px",
                                                cursor: "pointer",
                                                fontSize: "12px",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px",
                                                color: "#f44336",
                                              }}
                                              onClick={() => {
                                                deleteProjectSectionData(section.id);
                                                setSectionMenu(null);
                                              }}
                                              onMouseEnter={(e) =>
                                              (e.target.style.backgroundColor =
                                                "#f5f5f5")
                                              }
                                              onMouseLeave={(e) =>
                                              (e.target.style.backgroundColor =
                                                "white")
                                              }
                                            >
                                              <FiTrash2 size={14} /> Delete
                                            </div>
                                          </div>
                                        )}
                                    </div>
                                  </>
                                )}
                              </Card.Header>
                              <Card.Body
                                className="overflow-auto"
                                style={{ height: "calc(100vh - 156px)" }}
                              >
                                <Droppable
                                  droppableId={section.id}
                                  type="TASK"
                                  direction="vertical"
                                >
                                  {(provided, snapshot) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.droppableProps}
                                      className="rounded"
                                      style={{
                                        backgroundColor: snapshot.isDraggingOver
                                          ? "#fff3e0"
                                          : "transparent",
                                        minHeight: "calc(100vh - 200px)",
                                        transition: "all 0.2s",
                                      }}
                                    >
                                      {section.tasks.map((task, taskIndex) => (
                                        <Draggable
                                          key={task.id}
                                          draggableId={task.id}
                                          index={taskIndex}
                                        >
                                          {(provided, snapshot) => (
                                            <Card
                                              ref={provided.innerRef}
                                              {...provided.draggableProps}
                                              {...provided.dragHandleProps}
                                              className="p-3 mb-3 border"
                                              style={{
                                                backgroundColor: snapshot.isDragging
                                                  ? "#fff9c4"
                                                  : "#ffffff",
                                                cursor: "grab",
                                                borderRadius: "8px",
                                                ...provided.draggableProps.style,
                                              }}
                                              onContextMenu={(e) =>
                                                handleContextMenu(e, "task", {
                                                  sectionId: section.id,
                                                  taskId: task.id,
                                                  task: task,
                                                })
                                              }
                                            >
                                              {editingTask ===
                                                `${section.id}-${task.id}` ? (
                                                <div>
                                                  <Form.Control
                                                    as="textarea"
                                                    value={editTaskTitle}
                                                    onChange={(e) =>
                                                      setEditTaskTitle(e.target.value)
                                                    }
                                                    size="sm"
                                                    className="flex-grow-1 fw-bold mb-1"
                                                    style={{ fontSize: "13px" }}
                                                  />
                                                  <div className="hstack gap-2 justify-content-end">
                                                    <Button
                                                      onClick={() =>
                                                        updateProjectTask(section.id, task.id)
                                                      }
                                                      variant="success"
                                                      size="sm"
                                                      style={{ fontSize: "12px" }}
                                                    >
                                                      <FiCheck />000
                                                    </Button>
                                                    <Button
                                                      onClick={cancelTaskEdit}
                                                      variant="danger"
                                                      size="sm"
                                                      style={{ fontSize: "12px" }}
                                                    >
                                                      <FiX />
                                                    </Button>
                                                  </div>
                                                </div>
                                              ) : (
                                                <>
                                                  {/* Task Header */}
                                                  <div className="d-flex justify-content-between align-items-start mb-2">
                                                    <div className="d-flex align-items-center gap-2 flex-grow-1">
                                                      <Form.Check
                                                        type="checkbox"
                                                        checked={task.done || false}
                                                        onChange={() =>
                                                          toggleTaskDone(
                                                            section.id,
                                                            task.id
                                                          )
                                                        }
                                                        style={{ marginBottom: 0 }}
                                                      />
                                                      <h6
                                                        className="m-0"
                                                        style={{
                                                          fontSize: "13px",
                                                          fontWeight: "600",
                                                          color: "#333",
                                                          textDecoration: task.done
                                                            ? "line-through"
                                                            : "none",
                                                          opacity: task.done
                                                            ? 0.6
                                                            : 1,
                                                          cursor: "pointer",
                                                        }}
                                                        onClick={() => {
                                                          setSelectedTaskDetail({
                                                            sectionId: section.id,
                                                            taskId: task.id,
                                                            task: task,
                                                          });
                                                        }}
                                                      >
                                                        {task.title}
                                                      </h6>
                                                    </div>
                                                    <Button
                                                      onClick={() =>
                                                        startEditTask(
                                                          section.id,
                                                          task.id,
                                                          task.title
                                                        )
                                                      }
                                                      variant="light"
                                                      size="sm"
                                                      style={{
                                                        color: "#999",
                                                        padding: "2px 4px",
                                                      }}
                                                      title="Edit task"
                                                    >
                                                      <FiEdit2 size={14} />
                                                    </Button>
                                                  </div>

                                                  {/* Task Info Row */}
                                                  <div className="d-flex align-items-center gap-2 mb-2">
                                                    <div className="d-flex align-items-center gap-1">
                                                      <span className="badge rounded-pill text-bg-primary fw-semibold px-2 py-1">
                                                        RC
                                                      </span>
                                                      <Button
                                                        variant="outline-secondary"
                                                        size="sm"
                                                        className="rounded-circle p-0 d-inline-flex align-items-center justify-content-center"
                                                        title="Assign user"
                                                      >
                                                        <FiPlus size={12} />
                                                      </Button>
                                                    </div>
                                                    <div className="ms-auto d-flex align-items-center gap-1 text-muted small">
                                                      <span>??</span>
                                                      <span>7</span>
                                                    </div>
                                                  </div>

                                                  {/* Subtasks Section */}
                                                  {task.subtasks &&
                                                    task.subtasks.length > 0 && (
                                                      <div
                                                        style={{
                                                          marginTop: "8px",
                                                          paddingTop: "8px",
                                                          borderTop: "1px solid #eee",
                                                        }}
                                                      >
                                                        {task.subtasks.map(
                                                          (subtask) => (
                                                            <div
                                                              key={subtask.id}
                                                              className="d-flex align-items-center gap-2 mb-2"
                                                              style={{
                                                                fontSize: "12px",
                                                              }}
                                                            >
                                                              {editingSubtaskId ===
                                                                subtask.id ? (
                                                                <>
                                                                  <Form.Control
                                                                    type="text"
                                                                    value={
                                                                      editingSubtaskText
                                                                    }
                                                                    onChange={(e) =>
                                                                      setEditingSubtaskText(
                                                                        e.target.value
                                                                      )
                                                                    }
                                                                    size="sm"
                                                                    style={{
                                                                      fontSize:
                                                                        "12px",
                                                                    }}
                                                                  />
                                                                  <Button
                                                                    onClick={() =>
                                                                      updateProjectSubtask(
                                                                        section.id,
                                                                        task.id,
                                                                        subtask.id,
                                                                        editingSubtaskText
                                                                      )
                                                                    }
                                                                    variant="success"
                                                                    size="sm"
                                                                    className="p-0 px-1 border-0"
                                                                    style={{
                                                                      fontSize:
                                                                        "10px",
                                                                    }}
                                                                  >
                                                                    <FiCheck />
                                                                  </Button>
                                                                  <Button
                                                                    onClick={() =>
                                                                      setEditingSubtaskId(
                                                                        null
                                                                      )
                                                                    }
                                                                    variant="danger"
                                                                    size="sm"
                                                                    className="p-0 px-1 border-0"
                                                                    style={{
                                                                      fontSize:
                                                                        "10px",
                                                                    }}
                                                                  >
                                                                    <FiX />
                                                                  </Button>
                                                                </>
                                                              ) : (
                                                                <>
                                                                  <Form.Check
                                                                    type="checkbox"
                                                                    style={{
                                                                      marginBottom: 0,
                                                                    }}
                                                                  />
                                                                  <span
                                                                    style={{
                                                                      cursor:
                                                                        "pointer",
                                                                      flex: 1,
                                                                    }}
                                                                    onClick={() => {
                                                                      setEditingSubtaskId(
                                                                        subtask.id
                                                                      );
                                                                      setEditingSubtaskText(
                                                                        subtask.title
                                                                      );
                                                                    }}
                                                                  >
                                                                    {subtask.title}
                                                                  </span>
                                                                  <span
                                                                    style={{
                                                                      fontSize:
                                                                        "11px",
                                                                      color: "#999",
                                                                      marginRight:
                                                                        "8px",
                                                                    }}
                                                                  >
                                                                    {subtask.date &&
                                                                      subtask.date}
                                                                  </span>
                                                                  <div
                                                                    style={{
                                                                      width: "20px",
                                                                      height: "20px",
                                                                      borderRadius:
                                                                        "50%",
                                                                      backgroundColor:
                                                                        "#d946ef",
                                                                      display: "flex",
                                                                      alignItems:
                                                                        "center",
                                                                      justifyContent:
                                                                        "center",
                                                                      fontSize: "8px",
                                                                      fontWeight:
                                                                        "bold",
                                                                      color: "white",
                                                                    }}
                                                                  >
                                                                    RC
                                                                  </div>
                                                                  <Button
                                                                    onClick={() =>
                                                                      deleteProjectSubtask(
                                                                        section.id,
                                                                        task.id,
                                                                        subtask.id
                                                                      )
                                                                    }
                                                                    variant="light"
                                                                    size="sm"
                                                                    className="p-0 border-0"
                                                                    style={{
                                                                      fontSize:
                                                                        "10px",
                                                                      color:
                                                                        "#f44336",
                                                                    }}
                                                                  >
                                                                    <FiTrash2 />
                                                                  </Button>
                                                                </>
                                                              )}
                                                            </div>
                                                          )
                                                        )}
                                                      </div>
                                                    )}

                                                  {/* Add Subtask Button */}
                                                  <Button
                                                    onClick={() =>
                                                      addProjectSubtask(section.id, task.id)
                                                    }
                                                    variant="light"
                                                    size="sm"
                                                    style={{
                                                      fontSize: "12px",
                                                      color: "#2196F3",
                                                      marginTop: "8px",
                                                    }}
                                                  >
                                                    <FiPlus size={14} /> Add subtask
                                                  </Button>

                                                  {/* Action Buttons */}
                                                  <div
                                                    className="hstack gap-1 mt-2"
                                                    style={{
                                                      justifyContent: "flex-end",
                                                      borderTop: "1px solid #eee",
                                                      paddingTop: "8px",
                                                    }}
                                                  >
                                                    <Button
                                                      variant="light"
                                                      size="sm"
                                                      style={{
                                                        color: "#999",
                                                        fontSize: "11px",
                                                      }}
                                                      title="Assign"
                                                    >
                                                      <FiUser size={12} />
                                                    </Button>
                                                    <Button
                                                      variant="light"
                                                      size="sm"
                                                      style={{
                                                        color: "#999",
                                                        fontSize: "11px",
                                                      }}
                                                      title="Assign date"
                                                    >
                                                      <FiCalendar size={12} />
                                                    </Button>
                                                    <Button
                                                      onClick={() =>
                                                        duplicateProjectTask(
                                                          section.id,
                                                          task.id
                                                        )
                                                      }
                                                      variant="light"
                                                      size="sm"
                                                      style={{
                                                        color: "#2196F3",
                                                        fontSize: "11px",
                                                      }}
                                                      title="Duplicate task"
                                                    >
                                                      <FiCopy size={12} />
                                                    </Button>
                                                    <Button
                                                      onClick={() =>
                                                        deleteProjectTask(
                                                          section.id,
                                                          task.id
                                                        )
                                                      }
                                                      variant="light"
                                                      size="sm"
                                                      style={{
                                                        color: "#f44336",
                                                        fontSize: "11px",
                                                      }}
                                                    >
                                                      <FiTrash2 size={12} />
                                                    </Button>
                                                  </div>
                                                </>
                                              )}
                                            </Card>
                                          )}
                                        </Draggable>
                                      ))}
                                      {provided.placeholder}
                                      <div style={{ padding: "8px 0" }}>
                                        {showTaskInput[section.id] ? (
                                          <div className="d-flex gap-1 px-3">
                                            <Form.Control
                                              type="text"
                                              placeholder="Add task..."
                                              value={newTaskInput[section.id] || ""}
                                              onChange={(e) =>
                                                setNewTaskInput({
                                                  ...newTaskInput,
                                                  [section.id]: e.target.value,
                                                })
                                              }
                                              onKeyPress={(e) => {
                                                if (e.key === "Enter") {
                                                  addUpdateProjectTask(section.id);
                                                  setShowTaskInput({
                                                    ...showTaskInput,
                                                    [section.id]: false,
                                                  });
                                                }
                                              }}
                                              size="sm"
                                              style={{ fontSize: "12px" }}
                                              autoFocus
                                            />
                                            <Button
                                              onClick={() => {
                                                addUpdateProjectTask(section.id);
                                                setShowTaskInput({
                                                  ...showTaskInput,
                                                  [section.id]: false,
                                                });
                                              }}
                                              variant="success"
                                              size="sm"
                                            >
                                              <FiCheck />
                                            </Button>
                                            <Button
                                              onClick={() =>
                                                setShowTaskInput({
                                                  ...showTaskInput,
                                                  [section.id]: false,
                                                })
                                              }
                                              variant="danger"
                                              size="sm"
                                            >
                                              <FiX />
                                            </Button>
                                          </div>
                                        ) : (
                                          <Button
                                            onClick={() =>
                                              setShowTaskInput({
                                                ...showTaskInput,
                                                [section.id]: true,
                                              })
                                            }
                                            variant="outline-primary"
                                            size="sm"
                                            className="w-100"
                                            style={{ fontSize: "12px" }}
                                          >
                                            <FiPlus /> Add Task
                                          </Button>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </Droppable>
                              </Card.Body>
                            </Card>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>
            </DragDropContext>
          </Container>
          {selectedTaskDetail && (
            <TaskThreadPanel
              task={getTaskById(
                selectedTaskDetail.sectionId,
                selectedTaskDetail.taskId
              )}
              sectionId={selectedTaskDetail.sectionId}
              onClose={() => setSelectedTaskDetail(null)}
              onAddProjectSubtask={addProjectSubtask}
              onDeleteProjectSubtask={deleteProjectSubtask}
              onToggleSubtask={(sectionId, taskId, subtaskId) => {
                const updatedSections = sections.map((section) => {
                  if (section.id === sectionId) {
                    return {
                      ...section,
                      tasks: section.tasks.map((task) => {
                        if (task.id === taskId) {
                          return {
                            ...task,
                            subtasks: task.subtasks.map((subtask) =>
                              subtask.id === subtaskId
                                ? { ...subtask, done: !subtask.done }
                                : subtask
                            ),
                          };
                        }
                        return task;
                      }),
                    };
                  }
                  return section;
                });
                setSections(updatedSections);
              }}
              onEditSubtask={(subtask) => {
                setEditingSubtaskId(subtask.id);
                setEditingSubtaskText(subtask.title);
              }}
              onToggleTask={toggleTaskDone}
              onDeleteProjectTask={deleteProjectTask}
              onDuplicateProjectTask={duplicateProjectTask}
            />
          )}
        </>
        :
        <div
          style={{
            padding: "8px 12px",
            cursor: "pointer",
            fontSize: "12px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            borderBottom: "1px solid #eee",
          }}
          onClick={() => {
            addNewProjectSection();
          }}
        >
          <FiPlus size={14} /> Add New Section
        </div>
      }
    </div>
  );
};

export default ProjectManagement;

